import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Input, Select, Textarea, Tabs, Stepper, Badge, ProgressBar } from '@dreamtree-org/twreact-ui';
import { ArrowRight, ArrowLeft, ClipboardList, Check, X, Receipt, ShieldCheck, Hand, Lock } from 'lucide-react';
import { PageHeader, SectionCard, QueryState, KeyValue, Money } from '@/components';
import { useQuery } from '@/lib/useQuery';
import { formatINR, formatDate } from '@/lib/format';
import { STAGES, STAGE_LABEL, QC_CHECKS, nextStages, canTransition, latestEstimate, hoursLeft, isValidOtp } from '../domain';
import { StageBadge, SlaBadge, PriorityBadge, EstimateBadge, RestrictionsBanner, useAction, ActionResult, formatDateTime, OTP_HINT, HandoverCode } from '../components';

const PAY_METHODS = ['UPI', 'cash', 'card'].map((m) => ({ value: m, label: m === 'UPI' ? 'UPI' : m[0].toUpperCase() + m.slice(1) }));

const ORDER = Object.fromEntries(STAGES.map((s, i) => [s.key, i]));

function StageMoves({ job, act, reload }) {
  const moves = nextStages(job.stage).map((to) => ({ to, ...canTransition(job, to) }));
  if (!moves.length) return null;
  const move = async (to) => {
    const res = await act.run('serviceJobMove', { id: job.id, to }, `Moved to ${STAGE_LABEL[to]}.`);
    if (res) reload();
  };
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-2">
        {moves.map((m) => {
          const back = ORDER[m.to] < ORDER[job.stage];
          return (
            <Button
              key={m.to}
              variant={back ? 'ghost' : 'primary'}
              disabled={!m.ok}
              loading={act.busy === 'serviceJobMove'}
              onClick={() => move(m.to)}
              leftIcon={back ? <ArrowLeft size={16} /> : null}
              rightIcon={back ? null : <ArrowRight size={16} />}
              title={m.ok ? '' : m.reason}
            >
              {back ? `Back to ${STAGE_LABEL[m.to]}` : `Move to ${STAGE_LABEL[m.to]}`}
            </Button>
          );
        })}
      </div>
      {moves
        .filter((m) => !m.ok && ORDER[m.to] > ORDER[job.stage])
        .map((m) => (
          <p key={m.to} className="text-xs opacity-80 flex items-center gap-1">
            <Lock size={12} aria-hidden /> {STAGE_LABEL[m.to]}: {m.reason}
          </p>
        ))}
    </div>
  );
}

function PartsCard({ job, act, reload }) {
  const parts = useQuery('serviceParts', null, { select: (r) => r.data });
  const est = latestEstimate(job);
  const quoted = new Set(((est && est.lines) || []).filter((l) => l.partId).map((l) => l.partId));
  const [partId, setPartId] = useState('');
  const [qty, setQty] = useState('1');
  const open = ['in_repair', 'qc'].includes(job.stage);
  const options = (parts.data || [])
    .slice()
    .sort((a, b) => Number(quoted.has(b.id)) - Number(quoted.has(a.id)))
    .map((p) => ({ value: p.id, label: `${quoted.has(p.id) ? '★ ' : ''}${p.sku} · ${p.description} (${p.free} free)` }));
  const record = async () => {
    const res = await act.run('serviceJobPartsUsage', { id: job.id, partId, quantity: Number(qty) }, 'Part usage recorded and stock updated.');
    if (res) {
      setPartId('');
      setQty('1');
      reload();
      parts.reload();
    }
  };
  return (
    <SectionCard title="Parts used">
      {job.partsUsed.length ? (
        <ul className="divide-y divide-[var(--app-border)] text-sm">
          {job.partsUsed.map((u) => (
            <li key={u.id} className="py-2 flex justify-between gap-2">
              <span className="min-w-0">
                {u.quantity} × {u.description} <span className="font-mono text-xs opacity-70">{u.sku}</span>
              </span>
              <span className="text-xs opacity-70 shrink-0">{formatDateTime(u.at)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm opacity-70">No parts used yet.</p>
      )}
      {job.reservations.length ? (
        <div className="text-xs opacity-80">
          Reserved for this job: {job.reservations.map((r) => `${r.quantity} × ${r.part.sku}`).join(', ')}
        </div>
      ) : null}
      {open ? (
        <div className="grid grid-cols-[minmax(0,1fr)_5rem] gap-2 items-end pt-2 border-t border-[var(--app-border)]">
          <Select label="Part (★ on the approved quote)" options={options} value={partId} onChange={setPartId} searchable closeOnSelect placeholder="Choose a part" />
          <Input label="Qty" type="number" value={qty} onChange={(e) => setQty(e.target.value)} />
          <Button className="col-span-2 justify-self-start" onClick={record} disabled={!partId || !(Number(qty) > 0)} loading={act.busy === 'serviceJobPartsUsage'}>
            Record usage
          </Button>
        </div>
      ) : (
        <p className="text-xs opacity-70">Parts are recorded while the job is in repair or QC.</p>
      )}
    </SectionCard>
  );
}

function WorklogCard({ job, act, reload }) {
  const [activity, setActivity] = useState('');
  const [minutes, setMinutes] = useState('30');
  const [notes, setNotes] = useState(job.labourNotes || '');
  const add = async () => {
    const res = await act.run('serviceJobWorklog', { id: job.id, activity, minutes: Number(minutes) }, 'Worklog added.');
    if (res) {
      setActivity('');
      reload();
    }
  };
  const saveNotes = async () => {
    const res = await act.run('serviceJobLabourNotes', { id: job.id, labourNotes: notes }, 'Labour notes saved.');
    if (res) reload();
  };
  const total = job.worklog.reduce((s, w) => s + w.minutes, 0);
  return (
    <SectionCard title="Worklog and labour notes" actions={<span className="text-xs opacity-70">{Math.round((total / 60) * 10) / 10} h logged</span>}>
      {job.worklog.length ? (
        <ul className="divide-y divide-[var(--app-border)] text-sm">
          {job.worklog.map((w) => (
            <li key={w.id} className="py-2 flex justify-between gap-2">
              <span className="min-w-0">
                {w.activity}
                <span className="block text-xs opacity-70">
                  {w.actor} · {formatDateTime(w.at)}
                </span>
              </span>
              <span className="tabular-nums shrink-0">{w.minutes} min</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm opacity-70">Nothing logged yet.</p>
      )}
      {job.stage !== 'closed' ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_6rem_auto] items-end">
          <Input label="Activity" value={activity} onChange={(e) => setActivity(e.target.value)} placeholder="e.g. Replaced keyboard, tested all keys" />
          <Input label="Minutes" type="number" value={minutes} onChange={(e) => setMinutes(e.target.value)} />
          <Button variant="outline" onClick={add} disabled={!activity.trim() || !(Number(minutes) > 0)} loading={act.busy === 'serviceJobWorklog'}>
            Add
          </Button>
        </div>
      ) : null}
      <Textarea label="Labour notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What was done and how; anything the next technician should know." />
      <div>
        <Button size="sm" variant="ghost" onClick={saveNotes} disabled={notes === (job.labourNotes || '')} loading={act.busy === 'serviceJobLabourNotes'}>
          Save notes
        </Button>
      </div>
    </SectionCard>
  );
}

function QcCard({ job, act, reload }) {
  const passed = QC_CHECKS.filter((c) => job.qc[c.code] === 'pass').length;
  const editable = job.stage === 'qc';
  const set = async (code, result) => {
    const res = await act.run('serviceJobQc', { id: job.id, checks: { [code]: result } }, '', `qc-${code}`);
    if (res) reload();
  };
  return (
    <SectionCard title="QC checklist" actions={<span className="text-sm tabular-nums">{passed}/{QC_CHECKS.length} passed</span>}>
      <ProgressBar value={passed} max={QC_CHECKS.length} size="sm" ariaLabel="QC progress" />
      {!editable ? <p className="text-xs opacity-70">{ORDER[job.stage] < ORDER.qc ? 'Checks open when the job reaches QC.' : 'QC is finished for this job.'}</p> : null}
      <ul className="divide-y divide-[var(--app-border)]">
        {QC_CHECKS.map((c) => {
          const r = job.qc[c.code];
          return (
            <li key={c.code} className="py-2 flex flex-wrap items-center gap-2">
              <span className="flex-1 min-w-[12rem] text-sm">{c.label}</span>
              {r ? <Badge variant={r === 'pass' ? 'success' : 'error'} size="sm">{r === 'pass' ? 'Pass' : 'Fail'}</Badge> : <Badge size="sm">Not checked</Badge>}
              {editable ? (
                <span className="flex gap-1">
                  <Button size="sm" variant={r === 'pass' ? 'success' : 'outline'} onClick={() => set(c.code, 'pass')} aria-label={`${c.label}: pass`} loading={act.busy === `qc-${c.code}`}>
                    <Check size={14} />
                  </Button>
                  <Button size="sm" variant={r === 'fail' ? 'destructive' : 'outline'} onClick={() => set(c.code, 'fail')} aria-label={`${c.label}: fail`}>
                    <X size={14} />
                  </Button>
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </SectionCard>
  );
}

function CompletionCard({ job, act, reload }) {
  const [summary, setSummary] = useState(job.completion ? job.completion.summary : '');
  const [otp, setOtp] = useState('');
  const canWrite = ['qc', 'ready_for_return'].includes(job.stage);
  const save = async () => {
    const res = await act.run('serviceJobComplete', { id: job.id, summary }, 'Completion report saved.');
    if (res) reload();
  };
  const ack = async () => {
    const res = await act.run('serviceJobReturnAck', { id: job.id, otp }, 'Return acknowledged. The job can now be closed.');
    if (res) reload();
  };
  return (
    <SectionCard title="Service completion report">
      {job.completion ? <p className="text-xs opacity-70">Last saved {formatDateTime(job.completion.at)}</p> : null}
      <Textarea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} disabled={!canWrite} placeholder="Root cause, what was replaced, test results, advice to the customer." aria-label="Completion summary" />
      {canWrite ? (
        <div>
          <Button variant="outline" onClick={save} disabled={!summary.trim() || summary === (job.completion && job.completion.summary)} loading={act.busy === 'serviceJobComplete'}>
            Save report
          </Button>
        </div>
      ) : null}
      {job.stage === 'ready_for_return' ? (
        <div className="pt-3 border-t border-[var(--app-border)] space-y-2">
          <div className="font-medium text-sm">Return handover</div>
          {job.returnSchedule ? (
            <p className="text-sm opacity-80">
              Customer chose {job.returnSchedule.mode === 'delivery' ? 'doorstep delivery' : 'to collect at the store'}: {formatDate(job.returnSchedule.slot.date)}, {job.returnSchedule.slot.label}.
            </p>
          ) : (
            <p className="text-sm opacity-70">The customer has not chosen how to get the device back yet (they do it from the storefront tracker).</p>
          )}
          {job.returnAck ? (
            <Badge variant="success">Customer acknowledged the return ({job.returnAckRef})</Badge>
          ) : (
            <div className="space-y-2">
              <HandoverCode purpose="return" id={job.id} />
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-[12rem_auto] items-end">
                <Input label="Customer’s code" type="tel" inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} helperText={OTP_HINT} />
                <Button onClick={ack} disabled={!isValidOtp(otp)} loading={act.busy === 'serviceJobReturnAck'} leftIcon={<Hand size={16} />}>
                  Record return
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </SectionCard>
  );
}

function InvoiceCard({ job, act, reload }) {
  const inv = job.invoice;
  const [method, setMethod] = useState('UPI');
  const create = async () => {
    const res = await act.run('serviceInvoiceCreate', { jobId: job.id }, 'Service invoice issued.');
    if (res) reload();
  };
  const paid = async () => {
    const res = await act.run('serviceInvoiceMarkPaid', { jobId: job.id, method }, 'Payment recorded.');
    if (res) reload();
  };
  return (
    <SectionCard title="Service invoice" actions={inv ? <Badge variant={inv.paymentStatus === 'paid' ? 'success' : 'warning'} size="sm">{inv.paymentStatus === 'paid' ? 'Paid' : 'Payment pending'}</Badge> : null}>
      {inv ? (
        <>
          <KeyValue
            items={[
              { label: 'Invoice', value: inv.number },
              { label: 'Issued', value: formatDate(inv.issuedAt) },
              inv.paidAt ? { label: 'Paid', value: `${formatDate(inv.paidAt)} · ${inv.paymentMethod}` } : null,
            ]}
          />
          <ul className="text-sm space-y-1 border-t border-[var(--app-border)] pt-2">
            {inv.lines.map((l) => (
              <li key={l.id} className="flex justify-between gap-2">
                <span className="min-w-0">
                  {l.quantity} × {l.description}
                </span>
                <span className="tabular-nums shrink-0">{formatINR(l.quantity * l.unitPricePaise)}</span>
              </li>
            ))}
          </ul>
          <KeyValue
            items={[
              { label: 'Subtotal', value: <Money paise={inv.partsPaise + inv.labourPaise} /> },
              { label: 'GST (18%)', value: <Money paise={inv.taxPaise} /> },
              { label: 'Total', value: <Money paise={inv.totalPaise} className="font-bold" /> },
            ]}
          />
          {inv.paymentStatus !== 'paid' ? (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[10rem_auto] items-end">
              <Select label="Paid by" options={PAY_METHODS} value={method} onChange={(v) => setMethod(v || 'UPI')} closeOnSelect />
              <Button variant="outline" onClick={paid} loading={act.busy === 'serviceInvoiceMarkPaid'}>
                Record payment received
              </Button>
            </div>
          ) : null}
        </>
      ) : (
        <>
          <p className="text-sm opacity-70">
            {['ready_for_return', 'closed'].includes(job.stage)
              ? 'Issue the invoice from the approved quote (only the diagnostic fee if the quote was declined).'
              : 'The invoice is issued once the device is ready for return.'}
          </p>
          <Button onClick={create} disabled={!['ready_for_return', 'closed'].includes(job.stage)} loading={act.busy === 'serviceInvoiceCreate'} leftIcon={<Receipt size={16} />}>
            Issue service invoice
          </Button>
        </>
      )}
    </SectionCard>
  );
}

function WarrantyCard({ job }) {
  const w = job.warranty;
  return (
    <SectionCard title="Post-repair warranty" actions={w ? <Badge variant={w.status === 'active' ? 'success' : 'default'} size="sm">{w.status}</Badge> : null}>
      {w ? (
        <KeyValue
          items={[
            { label: 'Warranty', value: w.id },
            { label: 'Coverage', value: w.coverage },
            { label: 'Term', value: `${w.termDays} days` },
            { label: 'Valid', value: `${formatDate(w.startsAt)} – ${formatDate(w.endsAt)}` },
            { label: 'Policy', value: w.policyVersion },
          ]}
        />
      ) : (
        <p className="text-sm opacity-70 flex items-start gap-2">
          <ShieldCheck size={16} className="shrink-0 mt-0.5" aria-hidden />
          A 90-day parts-and-labour warranty starts when an approved repair is closed.
        </p>
      )}
    </SectionCard>
  );
}

/** SERV-03 Work order detail (R-04, R-05). */
export default function JobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const q = useQuery('serviceJob', { id }, { select: (r) => r.data });
  const techs = useQuery('serviceTechnicians', null, { select: (r) => r.data });
  const act = useAction();

  const assign = async (technicianId) => {
    if (!technicianId) return;
    const res = await act.run('serviceJobAssign', { id, technicianId }, 'Technician assigned.');
    if (res) q.reload();
  };

  return (
    <div className="space-y-4">
      <QueryState query={q}>
        {(job) => {
          const est = latestEstimate(job);
          const h = hoursLeft(job);
          const step = ORDER[job.stage];
          return (
            <>
              <PageHeader
                title={`Work order ${job.id}`}
                subtitle={`${job.device.model} · ${job.problem}`}
                back={{ label: 'Workbench', href: '/workbench' }}
                actions={
                  <Button variant="outline" onClick={() => nav(`/jobs/${job.id}/diagnosis`)} leftIcon={<ClipboardList size={16} />}>
                    Diagnosis & quote
                  </Button>
                }
              />
              <div className="overflow-x-auto">
                <Stepper
                  className="min-w-[40rem] py-2"
                  orientation="horizontal"
                  currentStep={step}
                  steps={STAGES.map((s, i) => ({ title: s.label, status: i < step || job.stage === 'closed' ? 'completed' : i === step ? 'current' : 'upcoming' }))}
                />
              </div>
              <StageMoves job={job} act={act} reload={q.reload} />
              <ActionResult action={act} />
              <RestrictionsBanner consent={job.restrictions} />

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <SectionCard title="Job">
                  <div className="flex flex-wrap gap-2">
                    <StageBadge stage={job.stage} />
                    <PriorityBadge priority={job.priority} />
                    <SlaBadge sla={job.sla} />
                  </div>
                  <KeyValue
                    items={[
                      { label: 'Due', value: `${formatDateTime(job.dueAt)}${job.sla !== 'done' ? ` (${h >= 0 ? `${h} h left` : `${-h} h overdue`})` : ''}` },
                      { label: 'Ticket', value: job.ticketId ? <button type="button" className="text-primary-600 hover:underline" onClick={() => nav(`/tickets/${job.ticketId}`)}>{job.ticketId}</button> : '—' },
                      job.warrantyClaimId ? { label: 'Warranty claim', value: job.warrantyClaimId } : null,
                      { label: 'Planned', value: `${job.plannedHours} h` },
                    ]}
                  />
                </SectionCard>
                <SectionCard title="Customer and device">
                  <KeyValue
                    items={[
                      { label: 'Customer', value: job.customer.name },
                      { label: 'Phone', value: job.customer.phone },
                      { label: 'Area', value: job.customer.area },
                      { label: 'Device', value: job.device.model },
                      { label: 'Serial', value: <span className="font-mono">{job.device.serialMasked}</span> },
                    ]}
                  />
                </SectionCard>
                <SectionCard title="Technician and quote">
                  <Select
                    label="Assigned technician"
                    options={(techs.data || []).map((t) => ({ value: t.id, label: `${t.name} · ${t.percent}% booked` }))}
                    value={job.technicianId || ''}
                    onChange={assign}
                    closeOnSelect
                    disabled={job.stage === 'closed'}
                    placeholder="Unassigned"
                  />
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <span>{est ? `Quote v${est.version}` : 'No quote yet'}</span>
                    {est ? (
                      <span className="flex items-center gap-2">
                        <EstimateBadge status={est.status} />
                        <Money paise={est.totalPaise} className="font-semibold" />
                      </span>
                    ) : null}
                  </div>
                </SectionCard>
              </div>

              <Tabs orientation="horizontal" variant="line" className="space-y-4">
                <Tabs.List className="overflow-x-auto">
                  <Tabs.Tab>Repair work</Tabs.Tab>
                  <Tabs.Tab>QC & completion</Tabs.Tab>
                  <Tabs.Tab>Invoice & warranty</Tabs.Tab>
                  <Tabs.Tab>Activity</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panels>
                  <Tabs.Panel>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <PartsCard job={job} act={act} reload={q.reload} />
                      <WorklogCard key={job.labourNotes} job={job} act={act} reload={q.reload} />
                    </div>
                  </Tabs.Panel>
                  <Tabs.Panel>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <QcCard job={job} act={act} reload={q.reload} />
                      <CompletionCard key={job.completion ? job.completion.at : 'none'} job={job} act={act} reload={q.reload} />
                    </div>
                  </Tabs.Panel>
                  <Tabs.Panel>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <InvoiceCard job={job} act={act} reload={q.reload} />
                      <WarrantyCard job={job} />
                    </div>
                  </Tabs.Panel>
                  <Tabs.Panel>
                    <SectionCard title="Activity log">
                      <ol className="space-y-3">
                        {job.activity.map((a) => (
                          <li key={a.id} className="flex gap-3 text-sm">
                            <span className="mt-1.5 h-2 w-2 rounded-full bg-primary-600 shrink-0" aria-hidden />
                            <span className="min-w-0">
                              {a.text}
                              <span className="block text-xs opacity-70">
                                {a.actor} · {formatDateTime(a.at)}
                              </span>
                            </span>
                          </li>
                        ))}
                      </ol>
                    </SectionCard>
                  </Tabs.Panel>
                </Tabs.Panels>
              </Tabs>
            </>
          );
        }}
      </QueryState>
    </div>
  );
}
